import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { publicUrl } from '@/utils/publicUrl';

export default function CTA() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4pbbnbm5ik2goc1es10', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const wechatQr = t('cta.wechatQrSrc', { defaultValue: '' });

  return (
    <section
      id="contact"
      className="border-t border-cyan-500/10 py-24 bg-slate-950 relative overflow-hidden"
    >
      {/* 与「产品」同规则：平铺 slate-950 + 青线方格 (opacity-10, 50px) */}
      <div className="absolute inset-0 opacity-10" aria-hidden>
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(6, 182, 212, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full backdrop-blur-sm mb-6">
              <i className="ri-mail-line text-cyan-400"></i>
              <span className="text-cyan-400 text-sm font-medium">{t('cta.badge')}</span>
            </div>

            <h4 className="text-4xl lg:text-5xl font-black text-white mb-6 leading-tight">
              {t('cta.title')}
            </h4>

            <p className="text-lg text-gray-400 leading-relaxed mb-8">
              {t('cta.lead')}
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-cyan-500/20 border border-cyan-500/30 flex-shrink-0">
                  <i className="ri-phone-line text-xl text-cyan-400"></i>
                </div>
                <div>
                  <div className="text-white font-semibold mb-1">{t('cta.phoneLabel')}</div>
                  <div className="text-gray-400 text-sm">{t('cta.phoneValue')}</div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-cyan-500/20 border border-cyan-500/30 flex-shrink-0">
                  <i className="ri-mail-line text-xl text-cyan-400"></i>
                </div>
                <div>
                  <div className="text-white font-semibold mb-1">{t('cta.emailLabel')}</div>
                  <div className="text-gray-400 text-sm">{t('cta.emailValue')}</div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-cyan-500/20 border border-cyan-500/30 flex-shrink-0">
                  <i className="ri-wechat-line text-xl text-cyan-400"></i>
                </div>
                <div className="min-w-0">
                  <div className="text-white font-semibold mb-2">{t('cta.officeLabel')}</div>
                  {wechatQr ? (
                    <div>
                      <img
                        src={publicUrl(wechatQr)}
                        alt={t('cta.qrCodeAlt')}
                        className="w-32 h-32 sm:w-36 sm:h-36 rounded-lg border border-cyan-500/20 bg-white object-contain p-1"
                        width={144}
                        height={144}
                      />
                      <p className="text-gray-400 text-sm mt-2 max-w-sm">{t('cta.addressValue')}</p>
                    </div>
                  ) : (
                    <div className="text-gray-400 text-sm">{t('cta.addressValue')}</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Contact Form */}
          <div className="p-8 rounded-2xl bg-gradient-to-br from-slate-900/80 to-slate-900/40 border border-cyan-500/20 backdrop-blur-sm">
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                  {t('cta.formName')}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-cyan-500/20 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-400 transition-colors"
                  placeholder={t('cta.formNamePlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  {t('cta.formEmail')}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-cyan-500/20 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-400 transition-colors"
                  placeholder={t('cta.formEmailPlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                  {t('cta.formCompany')}
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-cyan-500/20 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-400 transition-colors"
                  placeholder={t('cta.formCompanyPlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                  {t('cta.formMessage')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-cyan-500/20 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-400 transition-colors resize-none"
                  placeholder={t('cta.formMessagePlaceholder')}
                ></textarea>
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {t('cta.charCount', { count: formData.message.length })}
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-gradient-to-r from-cyan-500 to-teal-500 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-cyan-500/50 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                {isSubmitting ? t('cta.submitting') : t('cta.submit')}
                <i className="ri-send-plane-fill"></i>
              </button>

              {submitStatus === 'success' && (
                <p className="text-green-400 text-sm text-center">{t('cta.success')}</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-400 text-sm text-center">{t('cta.error')}</p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
